import { defaultLocale, getLocaleFromPath, isSupportedLocale, stripLocalePrefix } from "./locales";

export const routeIds = {
  home: "home",
  expertise: "expertise",
  mobileApps: "mobileApps",
  backend: "backend",
  graphics: "graphics",
  projectManagement: "projectManagement",
  projects: "projects",
  team: "team",
  contact: "contact",
  legalNotice: "legalNotice",
  sitemap: "sitemap",
};

export const localizedRoutes = {
  [routeIds.home]: {
    fr: "/fr/",
    en: "/en/",
  },
  [routeIds.expertise]: {
    fr: "/fr/#expertises",
    en: "/en/#expertise",
  },
  [routeIds.mobileApps]: {
    fr: "/fr/expertises/apps-mobiles",
    en: "/en/expertise/mobile-apps",
  },
  [routeIds.backend]: {
    fr: "/fr/expertises/back-end",
    en: "/en/expertise/backend",
  },
  [routeIds.graphics]: {
    fr: "/fr/expertises/developpement-3d",
    en: "/en/expertise/3d-development",
  },
  [routeIds.projectManagement]: {
    fr: "/fr/expertises/gestion-de-projet",
    en: "/en/expertise/project-management",
  },
  [routeIds.projects]: {
    fr: "/fr/realisations",
    en: "/en/projects",
  },
  [routeIds.team]: {
    fr: "/fr/equipe",
    en: "/en/team",
  },
  [routeIds.contact]: {
    fr: "/fr/contact",
    en: "/en/contact",
  },
  [routeIds.legalNotice]: {
    fr: "/fr/mentions-legales",
    en: "/en/legal-notice",
  },
  [routeIds.sitemap]: {
    fr: "/fr/sitemap.xml",
    en: "/en/sitemap.xml",
  },
};

const internalRoutes = {
  [routeIds.home]: "/",
  [routeIds.mobileApps]: "/expertises/apps-mobiles",
  [routeIds.backend]: "/expertises/back-end",
  [routeIds.graphics]: "/expertises/developpement-3d",
  [routeIds.projectManagement]: "/expertises/gestion-de-projet",
  [routeIds.projects]: "/realisations",
  [routeIds.team]: "/equipe",
  [routeIds.contact]: "/contact",
  [routeIds.legalNotice]: "/mentions-legales",
  [routeIds.sitemap]: "/sitemap.xml",
};

const legacyFrenchRoutes = {
  "/": routeIds.home,
  "/expertises/apps-mobiles": routeIds.mobileApps,
  "/expertises/back-end": routeIds.backend,
  "/expertises/developpement-3d": routeIds.graphics,
  "/expertises/gestion-de-projet": routeIds.projectManagement,
  "/realisations": routeIds.projects,
  "/equipe": routeIds.team,
  "/contact": routeIds.contact,
  "/mentions-legales": routeIds.legalNotice,
  "/sitemap.xml": routeIds.sitemap,
};

function normalizePath(path) {
  const pathname = (path || "/").split("#")[0].split("?")[0] || "/";
  return pathname !== "/" ? pathname.replace(/\/$/, "") : pathname;
}

const routesByLocale = Object.entries(localizedRoutes).reduce((acc, [routeId, paths]) => {
  Object.entries(paths).forEach(([locale, path]) => {
    if (path.includes("#")) {
      return;
    }

    acc[locale] = acc[locale] ?? {};
    acc[locale][normalizePath(path)] = routeId;
  });

  return acc;
}, {});

export function pathFor(routeId, locale = defaultLocale) {
  const safeLocale = isSupportedLocale(locale) ? locale : defaultLocale;
  return localizedRoutes[routeId]?.[safeLocale] ?? localizedRoutes[routeIds.home][safeLocale];
}

export function projectPathFor(slug, locale = defaultLocale) {
  const basePath = pathFor(routeIds.projects, locale);
  return `${basePath}/${slug}`;
}

export function routeIdForPath(path) {
  const locale = getLocaleFromPath(path);
  const normalizedPath = normalizePath(path);

  return routesByLocale[locale]?.[normalizedPath] ?? legacyFrenchRoutes[normalizedPath] ?? null;
}

export function internalPathFor(path) {
  const routeId = routeIdForPath(path);

  if (routeId) {
    return internalRoutes[routeId] ?? "/";
  }

  const locale = getLocaleFromPath(path);
  const cleanPath = stripLocalePrefix(normalizePath(path));
  const sourceProjectsPath = stripLocalePrefix(pathFor(routeIds.projects, locale));

  if (cleanPath.startsWith(`${sourceProjectsPath}/`)) {
    const slug = cleanPath.substring(sourceProjectsPath.length + 1);
    return `${internalRoutes[routeIds.projects]}/${slug}`;
  }

  return cleanPath;
}

export function alternatePathFor(path, targetLocale) {
  const safeTargetLocale = isSupportedLocale(targetLocale) ? targetLocale : defaultLocale;
  const locale = getLocaleFromPath(path);
  const routeId = routeIdForPath(path);

  if (routeId) {
    return pathFor(routeId, safeTargetLocale);
  }

  const sourceProjectsPath = pathFor(routeIds.projects, locale);
  const cleanPath = stripLocalePrefix(normalizePath(path));

  if (cleanPath.startsWith(`${stripLocalePrefix(sourceProjectsPath)}/`)) {
    const slug = cleanPath.substring(stripLocalePrefix(sourceProjectsPath).length + 1);
    return projectPathFor(slug, safeTargetLocale);
  }

  return pathFor(routeIds.home, safeTargetLocale);
}
